import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronDown, Check } from 'lucide-react';
import {
  addToCart,
  getExistingCartItemById,
  getExistingCartItemByIdAndVariation,
} from '../../features/cart/cartSlice';
import { removeCoupon } from '../../features/cart/couponSlice';
import { formatCurrency } from '../FormatCurrency';
import { calculateInstallmentBreakdown } from '../calculateInstallmentBreakdown';
import TermMismatchModal from '../TermMismatchModal';
import { getPaymentOptions } from '../paymentOptions';

const PLAN_LABELS = {
  full: 'Pay in full',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

const PLAN_UNITS = {
  full: 'One time',
  daily: 'per day',
  weekly: 'per week',
  monthly: 'per month',
};

export const AddToCartModal = ({ isOpen, onClose, product }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const existing = useSelector((state) =>
    getExistingCartItemByIdAndVariation(
      product?.productID,
      product?.selectedVariation
    )(state)
  );
  const [selectedTermMonths, setSelectedTermMonths] = useState(1);
  useEffect(() => {
    setSelectedTermMonths(1);
  }, [product?.productID, product?.selectedVariation]);

  const [mismatchError, setMismatchError] = useState(null);
  const [durationOpen, setDurationOpen] = useState(false);
  const durationRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (durationRef.current && !durationRef.current.contains(e.target)) {
        setDurationOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const durationOptions = useMemo(() => {
    if (!product) return [];
    if (
      Array.isArray(product?.flexibleTermOptions) &&
      product.flexibleTermOptions.length > 0
    ) {
      return product.flexibleTermOptions.map((opt) => opt.termMonths);
    }
    const maxMonths = Number(product?.paymentPlanMonth) || 0;
    return Array.from({ length: Math.max(maxMonths, 0) }, (_, i) => i + 1);
  }, [product]);

  const plans = useMemo(() => {
    if (!product) return [];

    const options = getPaymentOptions(product);

    return options.map((opt) => {
      let price;
      if (opt.type === 'full') {
        const flexOpts = product?.flexibleTermOptions;
        if (Array.isArray(flexOpts) && flexOpts.length > 0) {
          const oneMonthTerm = flexOpts.find((o) => o.termMonths === 1);
          price =
            oneMonthTerm?.fairAppPrice || opt.totalPrice || opt.amount || 0;
        } else {
          price = opt.totalPrice || opt.amount || 0;
        }
      } else {
        const dynamic = calculateInstallmentBreakdown({
          ...product,
          selectedPaymentPlan: opt.type,
          termMonths: selectedTermMonths,
        });
        price = dynamic?.installmentAmount || opt.installmentAmount || 0;
      }

      return {
        type: opt.type,
        label: PLAN_LABELS[opt.type] || opt.type,
        price: `${formatCurrency(price)} ${PLAN_UNITS[opt.type] || ''}`,
        tag: opt.type === 'monthly' ? 'Popular' : null,
      };
    });
  }, [product, selectedTermMonths]);

  const canOnlyPayFull =
    plans.length === 0 || (plans.length === 1 && plans[0].type === 'full');

  const defaultPlan =
    plans.find((p) => p.type === 'monthly')?.type || plans[0]?.type || 'full';

  const [selectedPlan, setSelectedPlan] = useState(defaultPlan);

  useEffect(() => {
    const plan =
      plans.find((p) => p.type === 'monthly')?.type || plans[0]?.type || 'full';
    setSelectedPlan(plan);
  }, [plans]);

  if (!isOpen || !product) return null;

  const handleAddToCart = async () => {
    dispatch(removeCoupon());

    if (existing) {
      toast.warning('Item already in cart', {
        className:
          'bg-[var(--yellow-primary)] text-black text-sm px-1 py-1 rounded-md min-h-0',
      });
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      const productWithPlan = {
        ...product,
        selectedPaymentPlan: selectedPlan,
        termMonths: selectedTermMonths,
      };
      await dispatch(addToCart(productWithPlan)).unwrap();

      toast.success(
        <div className="flex items-center space-x-2 justify-between h-[42px]">
          <div className="flex space-x-1 items-center">
            <span className="font-light text-base">Item added to cart</span>
            <img src="/images/blackcheckcircle.svg" alt="icon" />
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-black">|</span>
            <button
              onClick={() => navigate('/cart-items')}
              className="underline text-base font-medium"
            >
              View cart
            </button>
          </div>
        </div>,
        {
          className: 'toast-yellow',
        }
      );

      onClose();
    } catch (error) {
      if (
        error?.type === 'TERM_MISMATCH' ||
        error?.type === 'FREQUENCY_MISMATCH'
      ) {
        setMismatchError(error);
      } else {
        toast.error(
          typeof error === 'string' ? error : 'Failed to add item to cart',
          {
            className:
              'bg-[var(--yellow-primary)] text-black text-sm px-1 py-1 rounded-md min-h-0',
          }
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white w-[95%] md:w-[376px] rounded-lg shadow-lg py-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-black"
        >
          ✕
        </button>

        {canOnlyPayFull ? (
          <h2 className="font-duplet text-xl font-semibold mb-2 px-6">
            Available plan
          </h2>
        ) : (
          <div className="px-6">
            <h2 className="font-duplet text-xl font-semibold mb-2">
              Choose duration and payment plan.
            </h2>
            <p className="text-xs mb-2">
              Select how long you would like to complete payment
            </p>
            <div className="mb-4">
              <div className="relative" ref={durationRef}>
                <button
                  type="button"
                  onClick={() => setDurationOpen((prev) => !prev)}
                  className="w-full flex items-center justify-between border border-gray-300 rounded-md p-3 text-sm bg-white hover:border-gray-400 transition cursor-pointer"
                >
                  <span className="text-gray-900">
                    {selectedTermMonths}{' '}
                    {selectedTermMonths === 1 ? 'month' : 'months'}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-gray-500 transition-transform duration-200 ${durationOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {durationOpen && (
                  <ul className="absolute mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-20 max-h-48 overflow-auto">
                    {durationOptions?.map((months) => (
                      <li key={months}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTermMonths(months);
                            setDurationOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition ${
                            selectedTermMonths === months
                              ? 'bg-[#FFF8CF] font-medium'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <span>
                            {months} {months === 1 ? 'month' : 'months'}
                          </span>
                          {selectedTermMonths === months && (
                            <Check size={16} className="text-[#222224]" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="bg-[#F6F6F6] h-[6px] w-full my-4"></div>

        <p className="font-duplet text-gray-700 mb-2 px-6 text-sm">
          Select how you want to pay
        </p>
        <div className="space-y-3 mb-5 px-6">
          {plans.map((plan) => (
            <label
              key={plan.type}
              className={`flex justify-between items-center border rounded-md p-3 cursor-pointer transition ${
                selectedPlan === plan.type
                  ? 'border-[#FFDE11] bg-[#FFF8CF]'
                  : 'border-[#737376] hover:border-gray-300'
              }`}
            >
              <div>
                <p className="font-duplet font-medium">{plan.label}</p>
                <p className="text-sm font-medium">{plan.price}</p>
              </div>

              <div className="flex items-center gap-2">
                {plan.tag && (
                  <span className="text-xs bg-[#FFDE11] px-2 py-0.5 rounded">
                    {plan.tag}
                  </span>
                )}
                <input
                  type="radio"
                  name="addToCartPlan"
                  checked={selectedPlan === plan.type}
                  onChange={() => setSelectedPlan(plan.type)}
                  className="accent-[#FFDE11]"
                />
              </div>
            </label>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 font-duplet font-medium flex justify-between items-center">
          <button onClick={onClose} className="underline">
            Cancel
          </button>
          <button
            onClick={handleAddToCart}
            disabled={isSubmitting || !selectedPlan}
            className="bg-[#FFDE11] text-black font-medium px-6 py-2 rounded hover:bg-yellow-300 disabled:opacity-60"
          >
            {isSubmitting ? 'Adding...' : 'Add to cart'}
          </button>
        </div>
      </div>
      <TermMismatchModal
        isOpen={!!mismatchError}
        onClose={() => setMismatchError(null)}
        error={mismatchError}
      />
    </div>
  );
};
